import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, channelName, inboxApi, ruleCondition, shadowedRuleIds,
  type AgentGroup, type AssignmentRule,
} from '@wa/shared';
import { RoutingJourney } from '@/team/RoutingJourney';

/**
 * Who gets the next conversation.
 *
 * Two lists on one page rather than two pages: a rule points at a group, so
 * writing a rule and seeing who is actually in that group is one task, and
 * splitting it across a tab means writing a rule that routes to nobody.
 */
/**
 * Lives as a tab on the Team screen rather than as a destination of its own.
 *
 * The rail holds five and Reports needed one; routing rules and agent groups are
 * team configuration, so this is where they belong rather than where they fitted.
 * Gated on automation.write, which is a different permission from the rest of that
 * screen — so somebody who can change routing but not manage people still gets here.
 */
export function RoutingPanel() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const rules = useQuery({ queryKey: ['assignment-rules'], queryFn: inboxApi.assignmentRules });
  const groups = useQuery({ queryKey: ['agent-groups'], queryFn: inboxApi.agentGroups });
  const agents = useQuery({ queryKey: ['agents'], queryFn: inboxApi.agents });
  const tags = useQuery({ queryKey: ['tags'], queryFn: inboxApi.tags });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['assignment-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['agent-groups'] });
  };

  const fail = (e: unknown, fallback: string) =>
    setError(e instanceof ApiError ? e.message : fallback);

  const createRule = useMutation({
    mutationFn: inboxApi.createAssignmentRule,
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => fail(e, 'Could not save the rule.'),
  });

  const updateRule = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: { isActive?: boolean; priority?: number } }) =>
      inboxApi.updateAssignmentRule(id, patch),
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not change the rule.'),
  });

  const deleteRule = useMutation({
    mutationFn: inboxApi.deleteAssignmentRule,
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not delete the rule.'),
  });

  const createGroup = useMutation({
    mutationFn: inboxApi.createAgentGroup,
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => fail(e, 'Could not create the group.'),
  });

  const deleteGroup = useMutation({
    mutationFn: inboxApi.deleteAgentGroup,
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not delete the group.'),
  });

  const setMembers = useMutation({
    mutationFn: ({ id, agentIds }: { id: number; agentIds: number[] }) =>
      inboxApi.setAgentGroupMembers(id, agentIds),
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not save the members.'),
  });

  // Sorted here rather than trusted from the API: the list is now numbered and
  // reordered by swapping priorities, and a row whose number does not match the
  // order it is checked in is worse than no number at all.
  const ruleList = [...(rules.data ?? [])].sort((a, b) => a.priority - b.priority || a.id - b.id);
  const shadowed = shadowedRuleIds(ruleList);

  return (
    // The rules and groups are steps 2 and 3 of the journey, not the whole screen.
    // They are unchanged; what changed is that they now sit inside a page that
    // says when they are consulted and what happens either side of them.
    <RoutingJourney>
      <div className="flex flex-col gap-4">
        {error && (
          <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}

        <RuleSection
          rules={ruleList}
          shadowed={shadowed}
          groups={groups.data ?? []}
          tags={tags.data ?? []}
          channels={channels.data ?? []}
          isBusy={createRule.isPending}
          onCreate={(rule) => createRule.mutate(rule)}
          onToggle={(id, isActive) => updateRule.mutate({ id, patch: { isActive } })}
          onReorder={(id, priority) => updateRule.mutate({ id, patch: { priority } })}
          onDelete={(id) => deleteRule.mutate(id)}
          onCreateGroup={(name) => createGroup.mutate(name)}
        />

        <GroupSection
          groups={groups.data ?? []}
          agents={agents.data ?? []}
          isBusy={createGroup.isPending}
          onCreate={(name) => createGroup.mutate(name)}
          onDelete={(id) => deleteGroup.mutate(id)}
          onSetMembers={(id, agentIds) => setMembers.mutate({ id, agentIds })}
        />
      </div>
    </RoutingJourney>
  );
}

// ------------------------------------------------------------------- rules --

interface RuleSectionProps {
  rules: AssignmentRule[];
  shadowed: Set<number>;
  groups: AgentGroup[];
  tags: { id: number; name: string }[];
  channels: { id: number; displayPhoneNumber: string; verifiedName?: string | null; label?: string | null }[];
  isBusy: boolean;
  onCreate: (rule: {
    name: string; priority?: number;
    matchChannelId?: number | null; matchTagId?: number | null;
    matchKeyword?: string | null; targetGroupId?: number | null;
  }) => void;
  onToggle: (id: number, isActive: boolean) => void;
  onReorder: (id: number, priority: number) => void;
  onDelete: (id: number) => void;
  onCreateGroup: (name: string) => void;
}

/**
 * Writing a rule.
 *
 * This was five labelled boxes in a row — Name, When, Value, To, Priority — which
 * is a database row rendered as a form. Nothing on it said what a rule *was*, the
 * "To" list held one option and looked broken, and Priority asked for a number
 * whose meaning was never stated anywhere.
 *
 * It is one sentence now. You complete it, and it reads back as what it does.
 * Three things went with the redesign:
 *
 * - **Name is optional.** It is derived from the condition as you build it, so
 *   the required field that stopped you writing a rule is gone.
 * - **Priority is not a number you type.** New rules go last; arrows move them.
 *   "Checked in the order shown" is the truth, and now it is also the interface.
 * - **The team list can create a team.** A dropdown whose only option is its own
 *   default is not a choice, and sending somebody to another section to make one
 *   is where the task got abandoned.
 */
function RuleSection({
  rules, shadowed, groups, tags, channels, isBusy,
  onCreate, onToggle, onReorder, onDelete, onCreateGroup,
}: RuleSectionProps) {
  const [kind, setKind] = useState<'any' | 'channel' | 'tag' | 'keyword'>('any');
  const [value, setValue] = useState('');
  const [groupId, setGroupId] = useState('');
  const [name, setName] = useState('');
  const [newTeam, setNewTeam] = useState<string | null>(null);

  // The sentence, as it currently reads. Also the rule's name when none is typed,
  // so the list stays readable without asking anybody to invent a label.
  const condition =
    kind === 'any' ? 'Everything else'
      : kind === 'channel' ? `Sent to ${(() => { const c = channels.find((x) => String(x.id) === value); return c ? channelName(c) : 'a number'; })()}`
        : kind === 'tag' ? `Tagged ${tags.find((t) => String(t.id) === value)?.name ?? 'a tag'}`
          : `Message contains “${value.trim()}”`;

  const ready = kind === 'any' || value.trim().length > 0;

  function submit() {
    onCreate({
      name: name.trim() || condition,
      // Last, always. Rules are read top to bottom and a new one has not earned a
      // place above rules that are already working.
      priority: (rules.at(-1)?.priority ?? 0) + 10,
      matchChannelId: kind === 'channel' ? Number(value) : null,
      matchTagId: kind === 'tag' ? Number(value) : null,
      matchKeyword: kind === 'keyword' ? value.trim() : null,
      targetGroupId: groupId ? Number(groupId) : null,
    });
    setKind('any'); setValue(''); setGroupId(''); setName('');
  }

  /** Swaps this rule's priority with its neighbour's, so the pair trade places. */
  function move(index: number, by: -1 | 1) {
    const here = rules[index];
    const there = rules[index + by];
    if (!here || !there) return;
    onReorder(here.id, there.priority);
    onReorder(there.id, here.priority);
  }

  return (
    <section className="rounded-(--radius-field) border border-(--color-line)">
      <h3 className="border-b border-(--color-line) px-3 py-2 text-small font-semibold sm:px-4">
        Checked in this order — the first one that matches wins
      </h3>

      <ul className="divide-y divide-(--color-line-dim)">
        {rules.length === 0 && (
          <li className="px-3 py-5 text-center text-small text-(--color-muted) sm:px-4">
            No rules yet, so nothing is assigned automatically and every conversation
            waits in Unassigned. One rule saying <strong>everything else &rarr; anyone
            available</strong> is enough to start.
          </li>
        )}

        {rules.map((rule, index) => (
          <li key={rule.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 sm:px-4">
            <span aria-hidden="true" className="grid size-5 shrink-0 place-items-center rounded-full bg-(--color-sunken) text-micro font-semibold text-(--color-muted) tabular-nums">
              {index + 1}
            </span>

            <div className="min-w-0 flex-1">
              <p className={['text-small', rule.isActive ? '' : 'text-(--color-faint) line-through'].join(' ')}>
                <span className="font-medium">{ruleCondition(rule)}</span>
                <span className="text-(--color-muted)"> &rarr; {rule.targetGroupLabel ?? 'anyone available'}</span>
              </p>
              {/* The name only when it says something the sentence does not. */}
              {rule.name !== ruleCondition(rule) && (
                <p className="truncate text-micro text-(--color-faint)">{rule.name}</p>
              )}
              {shadowed.has(rule.id) && (
                <p className="mt-0.5 text-micro text-(--color-warn)">
                  Never reached &mdash; an &ldquo;everything else&rdquo; rule above it matches first.
                </p>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-0.5">
              <Arrow dir="up" disabled={index === 0} label={`Move ${rule.name} earlier`} onClick={() => move(index, -1)} />
              <Arrow dir="down" disabled={index === rules.length - 1} label={`Move ${rule.name} later`} onClick={() => move(index, 1)} />

              <button
                type="button"
                role="switch"
                aria-checked={rule.isActive}
                aria-label={`${rule.name} is ${rule.isActive ? 'on' : 'off'}`}
                onClick={() => onToggle(rule.id, !rule.isActive)}
                className={[
                  'tap ml-1 rounded-(--radius-chip) border px-2 py-0.5 text-micro font-medium',
                  rule.isActive
                    ? 'border-(--color-brand) bg-(--color-brand-wash) text-(--color-brand)'
                    : 'border-(--color-line) text-(--color-muted) hover:bg-(--color-sunken)',
                ].join(' ')}
              >
                {rule.isActive ? 'On' : 'Off'}
              </button>

              <button
                type="button"
                onClick={() => onDelete(rule.id)}
                aria-label={`Delete ${rule.name}`}
                className="tap grid size-7 place-items-center rounded text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad)"
              >
                <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                  <path d="M6 6l8 8M14 6l-8 8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </li>
        ))}
      </ul>

      {/* The composer: a sentence with the controls inside it. */}
      <div className="border-t border-(--color-line) bg-(--color-sunken) px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-small">
          <span className="text-(--color-muted)">Send</span>

          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value as typeof kind); setValue(''); }}
            aria-label="Which conversations this rule catches"
            className="tap rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          >
            <option value="any">everything else</option>
            <option value="channel">chats on one number</option>
            <option value="tag">chats with a tag</option>
            <option value="keyword">messages containing a word</option>
          </select>

          {kind === 'keyword' && (
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="refund"
              aria-label="The word to look for"
              className="tap w-32 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
            />
          )}

          {kind === 'channel' && (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label="Which number"
              className="tap rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
            >
              <option value="">pick a number&hellip;</option>
              {channels.map((c) => <option key={c.id} value={c.id}>{channelName(c)}</option>)}
            </select>
          )}

          {kind === 'tag' && (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              aria-label="Which tag"
              className="tap rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
            >
              <option value="">pick a tag&hellip;</option>
              {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}

          <span className="text-(--color-muted)">to</span>

          <select
            value={groupId}
            onChange={(e) => {
              if (e.target.value === '__new__') { setNewTeam(''); return; }
              setGroupId(e.target.value);
            }}
            aria-label="Who handles it"
            className="tap rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          >
            <option value="">anyone available</option>
            {groups.map((g) => <option key={g.id} value={g.id}>the {g.name} team</option>)}
            {/* A dropdown whose only option is its own default is not a choice. */}
            <option value="__new__">a new team&hellip;</option>
          </select>

          <button
            type="button"
            disabled={!ready || isBusy}
            onClick={submit}
            className="tap ml-auto rounded-(--radius-field) bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
          >
            Add rule
          </button>
        </div>

        {newTeam !== null && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              autoFocus
              value={newTeam}
              onChange={(e) => setNewTeam(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTeam.trim()) { onCreateGroup(newTeam.trim()); setNewTeam(null); }
                if (e.key === 'Escape') setNewTeam(null);
              }}
              placeholder="Trade desk"
              aria-label="Name for the new team"
              className="tap w-44 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
            />
            <button
              type="button"
              disabled={!newTeam.trim() || isBusy}
              onClick={() => { onCreateGroup(newTeam.trim()); setNewTeam(null); }}
              className="tap rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2.5 py-1.5 text-small font-medium disabled:opacity-40"
            >
              Create it
            </button>
            <button
              type="button"
              onClick={() => setNewTeam(null)}
              className="tap px-1 text-small text-(--color-muted) hover:text-(--color-ink)"
            >
              Cancel
            </button>
            <span className="w-full text-micro text-(--color-muted)">
              It is created empty. Add people to it below, then point this rule at it.
            </span>
          </div>
        )}

        {/* Read back what is about to be created, in the same words the list uses.
            Somebody who cannot tell from a form what it will do can tell from this. */}
        <p className="mt-2 text-micro text-(--color-muted)">
          Adds rule {rules.length + 1}: <strong className="text-(--color-ink)">{condition}</strong>
          {' → '}
          <strong className="text-(--color-ink)">
            {groupId ? groups.find((g) => String(g.id) === groupId)?.name ?? 'a team' : 'anyone available'}
          </strong>
          {kind === 'any' && rules.some((r) => !r.matchChannelId && !r.matchTagId && !r.matchKeyword && r.isActive)
            && ' — there is already an “everything else” rule, and the first match wins, so this one will never be reached.'}
        </p>

        <details className="mt-2">
          <summary className="cursor-pointer text-micro text-(--color-muted)">Give it a different name</summary>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={condition}
            aria-label="Rule name"
            className="tap mt-1.5 w-full max-w-72 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
        </details>
      </div>
    </section>
  );
}

function Arrow({ dir, disabled, label, onClick }: {
  dir: 'up' | 'down'; disabled: boolean; label: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="tap grid size-7 place-items-center rounded text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink) disabled:opacity-25 disabled:hover:bg-transparent"
    >
      <svg viewBox="0 0 20 20" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {dir === 'up' ? <path d="M5 12.5 10 7.5l5 5" /> : <path d="M5 7.5 10 12.5l5-5" />}
      </svg>
    </button>
  );
}

// ------------------------------------------------------------------ groups --

interface GroupSectionProps {
  groups: AgentGroup[];
  agents: { id: number; displayName: string }[];
  isBusy: boolean;
  onCreate: (name: string) => void;
  onDelete: (id: number) => void;
  onSetMembers: (id: number, agentIds: number[]) => void;
}

function GroupSection({ groups, agents, isBusy, onCreate, onDelete, onSetMembers }: GroupSectionProps) {
  const [name, setName] = useState('');

  return (
    <section className="rounded-(--radius-field) border border-(--color-line)">
      <h3 className="border-b border-(--color-line) px-3 py-2 text-small font-semibold sm:px-4">Teams a rule can point at</h3>

      <ul className="divide-y divide-(--color-line-dim)">
        {groups.length === 0 && (
          <li className="px-3 py-4 text-small text-(--color-muted) sm:px-4">
            None yet — every rule goes to anyone available, which is what a single
            team wants. Make one when you need refunds to reach different people
            from orders.
          </li>
        )}

        {groups.map((group) => {
          const memberIds = new Set(group.members.map((m) => m.agentId));

          return (
            <li key={group.id} className="px-3 py-2.5 sm:px-4">
              <div className="flex items-center gap-2">
                <p className="min-w-0 flex-1 truncate text-small font-medium">{group.name}</p>
                <button
                  type="button"
                  onClick={() => onDelete(group.id)}
                  aria-label={`Delete ${group.name}`}
                  className="tap grid size-8 shrink-0 place-items-center rounded text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-bad)"
                >
                  <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                    <path d="M6 6l8 8M14 6l-8 8" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Availability shown beside each name, because a group whose members
                  are all Away routes to nobody, and that is invisible otherwise. */}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {agents.map((agent) => {
                  const member = memberIds.has(agent.id);
                  const available = group.members.find((m) => m.agentId === agent.id)?.available ?? false;

                  return (
                    <button
                      key={agent.id}
                      type="button"
                      aria-pressed={member}
                      onClick={() => onSetMembers(
                        group.id,
                        member
                          ? [...memberIds].filter((id) => id !== agent.id)
                          : [...memberIds, agent.id],
                      )}
                      className={[
                        'tap flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-small transition',
                        member
                          ? 'border-(--color-brand) bg-(--color-brand-wash) text-(--color-brand)'
                          : 'border-(--color-line) text-(--color-muted) hover:bg-(--color-sunken)',
                      ].join(' ')}
                    >
                      {member && (
                        <span
                          aria-hidden="true"
                          className={['size-1.5 shrink-0 rounded-full', available ? 'bg-(--color-good)' : 'bg-(--color-faint)'].join(' ')}
                        />
                      )}
                      <span className="max-w-36 truncate">{agent.displayName}</span>
                      {member && <span className="sr-only">{available ? ', available' : ', away'}</span>}
                    </button>
                  );
                })}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-end gap-2 border-t border-(--color-line) bg-(--color-sunken) px-3 py-3 sm:px-4">
        <Field label="New group" className="min-w-40 flex-1">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Trade desk"
            className="w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small"
          />
        </Field>
        <button
          type="button"
          disabled={name.trim().length === 0 || isBusy}
          onClick={() => { onCreate(name.trim()); setName(''); }}
          className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
        >
          Add group
        </button>
      </div>
    </section>
  );
}

function Field({ label, className, children }: {
  label: string; className?: string; children: React.ReactNode;
}) {
  return (
    <label className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</span>
      {children}
    </label>
  );
}
